/**
 * 活动流快照的持久化准备。
 *
 * ## 历史包袱（为什么这里曾叫"瘦身"）
 *
 * 早期实现会对快照做**深度截断**：任何超过 4000 字的字符串都被切掉并打上
 * "已截断"标记。当时的理由是硬约束 —— 保存请求把整个对话的 `messages` 与
 * `activity_snapshot` 一起 POST，快照里常带**整章正文**，单次请求到 1.13 MB，
 * 撞上 nginx 默认 `client_max_body_size 1m` 直接 413，对话静默存不进去。
 *
 * ## 现在为什么不截断了
 *
 * 那个 1m 约束已经不存在：生产 nginx 明确配置 `client_max_body_size 50m`，
 * 后端 `express.json({ limit: '50mb' })`（见 docs-private/PRODUCTION.md 的 Nginx 段）。
 * 而截断的代价是**永久性的数据缺损**：
 *
 * - 工具结果被切掉 → 事后无法复现"AI 当时读到了什么"，也就无法解释它的判断；
 * - 审批明细里的正文被切掉 → 无法审计"这次写入到底改了什么"；
 * - 这些数据**一旦截断就再也拿不回来**，且截断点与业务无关（纯字符数阈值）。
 *
 * 对于"任意设备、任意时间都能无损续接会话"这个目标，任何字符级截断都是**缺陷**
 * 而不是优化。因此现在只做**结构化归一**：原样保留，不裁剪内容。
 *
 * 同时，完整的事件流水改由 `ai_session_events` 表承载（只追加、永不截断），
 * 快照退化为**展示用的派生视图** —— 即使未来快照格式变化，历史也不会丢。
 *
 * ## 关于请求体积的保护
 *
 * 不再按字符数截断，但仍保留一道**软性熔断**：只有当序列化结果真的逼近后端
 * 50MB 上限时才报警并返回原始值（由调用方决定如何处理），避免出现"请求发出去了、
 * 被 HTTP 层拒掉、用户却看不到任何错误"的静默失败。
 */

/** 后端 express.json 上限（50mb）。留出余量，避免正好卡在上限。 */
export const REQUEST_SIZE_SOFT_LIMIT = 45 * 1024 * 1024;

/**
 * 历史常量：**已废弃**，仅为兼容旧测试/外部引用保留。
 * 现在任何长度的字符串都不会被截断。
 *
 * @deprecated 不再有任何字段受此限制；保留只为避免破坏性重命名。
 */
export const SNAPSHOT_STRING_LIMIT = Number.POSITIVE_INFINITY;

/** 历史常量：**已废弃**，现在不会再有截断标记写入数据。 */
export const TRUNCATED_MARK = '';

/**
 * 持久化前的快照归一化：**无损**，原样返回。
 *
 * 保留函数形态（而不是让调用方直接传原值）是为了留一个"以后真要加约束"的
 * 单一入口 —— 届时只需改这里，不必再满仓库找调用点。
 *
 * @param value 任意快照值
 * @returns 同一个值（不做深拷贝、不做截断）
 */
export function snapshotForPersist<T>(value: T): T {
  return value;
}

/**
 * @deprecated 用 {@link snapshotForPersist}。保留名字以兼容旧调用点，
 * 行为已改为**无损直通**（不再截断）。
 */
export function trimSnapshotValue<T>(value: T): T {
  return snapshotForPersist(value);
}

/**
 * 探测待发送体积是否逼近后端上限。
 *
 * 只做判断与告警，**不修改数据**：宁可让调用方决定，也不要悄悄丢内容。
 *
 * @returns 序列化后的字节数（估算：按 UTF-8 最坏情况 3 字节/字符上限计）
 */
export function estimatePayloadBytes(value: unknown): number {
  try {
    return JSON.stringify(value)?.length ?? 0;
  } catch {
    return 0;
  }
}

/**
 * 是否已逼近请求上限（超过即说明这次 POST 有被 HTTP 层拒绝的风险）。
 */
export function isPayloadNearLimit(value: unknown, limit = REQUEST_SIZE_SOFT_LIMIT): boolean {
  return estimatePayloadBytes(value) > limit;
}
